import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import odooApi from '../services/odooApi';
import {
  registerWithEmailPassword,
  sendFirebasePasswordReset,
  signInWithEmailPassword,
  signOutFromFirebase,
} from '../services/firebaseAuth';

const AuthContext = createContext();
const PREFERRED_CURRENCY_EVENT = 'velaris:preferred-currency';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const hydrateUser = useCallback((userData) => {
    if (!userData) {
      return null;
    }

    return {
      ...userData,
      favorites: userData.favorites || userData.favoriteCars || [],
    };
  }, []);

  const syncPreferredCurrency = useCallback((userData) => {
    const preferredCurrency = userData?.preferredCurrency;
    if (!preferredCurrency || typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(PREFERRED_CURRENCY_EVENT, {
        detail: preferredCurrency,
      }),
    );
  }, []);

  // Check for stored user on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('velaris_user');
      const token = localStorage.getItem('velaris_token');
      
      if (storedUser && token) {
        try {
          // Verify token with backend
          const currentUser = await odooApi.getCurrentUser();
          if (currentUser) {
            const nextUser = hydrateUser(currentUser);
            setUser(nextUser);
            setIsAuthenticated(true);
            syncPreferredCurrency(nextUser);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('velaris_user');
            localStorage.removeItem('velaris_token');
          }
        } catch (e) {
          // Try to use stored user if API fails
          try {
            const userData = JSON.parse(storedUser);
            const nextUser = hydrateUser(userData);
            setUser(nextUser);
            setIsAuthenticated(true);
            syncPreferredCurrency(nextUser);
          } catch {
            localStorage.removeItem('velaris_user');
            localStorage.removeItem('velaris_token');
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [hydrateUser, syncPreferredCurrency]);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('velaris_user', JSON.stringify(user));
    }
  }, [user]);

  const normalizeFirebaseAuthError = useCallback((error, fallback) => {
    const code = error?.code;
    switch (code) {
      case 'firebase_not_configured':
      case 'auth/configuration-not-found':
      case 'auth/operation-not-allowed':
        return { error: 'Firebase Auth is not configured', code };
      case 'auth/email-already-in-use':
        return { error: 'This email is already registered. Please sign in.', code };
      case 'auth/invalid-credential':
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return { error: 'Invalid email or password', code };
      case 'auth/email-not-verified':
        return {
          error: 'Please verify your email before signing in. We sent a new verification email. Check your inbox and spam/junk folder.',
          code,
        };
      case 'auth/too-many-requests':
        return { error: 'Too many attempts. Please try again later.', code };
      default:
        return { error: error?.message || fallback, code };
    }
  }, []);

  // Register new user with Firebase email/password. The Odoo user is created
  // after the email is verified and the user signs in for the first time.
  const register = useCallback(async (userData) => {
    try {
      await registerWithEmailPassword(userData);
      await signOutFromFirebase();
      return {
        success: true,
        needsEmailVerification: true,
      };
    } catch (e) {
      return { success: false, ...normalizeFirebaseAuthError(e, 'Registration failed') };
    }
  }, [normalizeFirebaseAuthError]);

  // Login with Firebase email/password, then exchange the verified Firebase ID
  // token for the existing Velaris/Odoo API token.
  const loginWithEmail = useCallback(async (email, password) => {
    try {
      const firebaseResult = await signInWithEmailPassword({ email, password });
      const result = await odooApi.loginSocial('firebase', {
        idToken: firebaseResult.idToken,
        tokenSource: 'firebase',
      });
      if (result.success) {
        const loggedInUser = hydrateUser(result.user);
        setUser(loggedInUser);
        setIsAuthenticated(true);
        syncPreferredCurrency(loggedInUser);
        return { success: true, user: loggedInUser };
      }
      await signOutFromFirebase();
      return { success: false, error: result.error || 'Login failed', code: result.code };
    } catch (e) {
      await signOutFromFirebase();
      return { success: false, ...normalizeFirebaseAuthError(e, 'Login failed') };
    }
  }, [hydrateUser, normalizeFirebaseAuthError, syncPreferredCurrency]);

  // Phone OTP auth is disabled in web/mobile auth flow. Low-level Odoo
  // endpoints remain untouched for backend/backward compatibility.

  // Social login (Google)
  const loginWithSocial = useCallback(async (provider, providerData) => {
    try {
      const result = await odooApi.loginSocial(provider, providerData);
      if (result.success) {
        const loggedInUser = hydrateUser(result.user);
        setUser(loggedInUser);
        setIsAuthenticated(true);
        syncPreferredCurrency(loggedInUser);
        return { success: true, user: loggedInUser };
      }
      return {
        success: false,
        error: result.error || 'Social login failed',
        code: result.code,
      };
    } catch (e) {
      return { success: false, error: e.message || 'Social login failed' };
    }
  }, [hydrateUser, syncPreferredCurrency]);

  // Logout
  const logout = useCallback(async () => {
    await odooApi.logout();
    await signOutFromFirebase();
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('velaris_user');
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    try {
      const result = await odooApi.updateProfile(updates);
      if (result.success) {
        const updatedUser = { ...user, ...result.user };
        setUser(updatedUser);
        syncPreferredCurrency(updatedUser);
        return { success: true, user: updatedUser };
      }
      return { success: false, error: result.error };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [syncPreferredCurrency, user]);

  // Add to favorites
  const addToFavorites = useCallback(async (carId) => {
    if (!user) return { success: false, error: 'Not authenticated' };
    
    try {
      const result = await odooApi.addFavorite(carId);
      if (result.success) {
        const favorites = (user.favorites || []).map(String);
        if (!favorites.includes(String(carId))) {
          const updatedUser = { ...user, favorites: [...favorites, carId] };
          setUser(updatedUser);
        }
      }
      return result;
    } catch (e) {
      return { success: false, error: e.message || 'Failed to add favorite' };
    }
  }, [user]);

  // Remove from favorites
  const removeFromFavorites = useCallback(async (carId) => {
    if (!user) return { success: false, error: 'Not authenticated' };
    
    try {
      const result = await odooApi.removeFavorite(carId);
      if (result.success) {
        const favorites = user.favorites || [];
        const updatedUser = { ...user, favorites: favorites.filter(id => String(id) !== String(carId)) };
        setUser(updatedUser);
      }
      return result;
    } catch (e) {
      return { success: false, error: e.message || 'Failed to remove favorite' };
    }
  }, [user]);

  // Check if car is in favorites
  const isFavorite = useCallback((carId) => {
    if (!user) return false;
    return (user.favorites || []).map(String).includes(String(carId));
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated,
    register,
    loginWithEmail,
    loginWithSocial,
    logout,
    updateProfile,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    requestPasswordReset: async (email) => {
      try {
        await sendFirebasePasswordReset(email);
        return { success: true };
      } catch (e) {
        return { success: false, ...normalizeFirebaseAuthError(e, 'Failed to send reset email') };
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
